import { useState, FormEvent, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "../../lib/supabaseClient";

const NAVY = "#2a2420";
const ORANGE = "#607840";

const HELLOS = [
  "Bonjour !",      // French
  "Xin chào!",      // Vietnamese
  "안녕하세요!",      // Korean
  "你好！",           // Mandarin
  "你好！",           // Cantonese
  "Kamusta!",       // Tagalog
  "¡Hola!",         // Spanish
  "Hello!",         // English
  "こんにちは！",     // Japanese
  "สวัสดี!",        // Thai
  "ສະບາຍດີ!",      // Lao
];

type LoginErrorInfo = {
  message: string;
  code?: string;
};

function getLoginNotice(error: string | LoginErrorInfo) {
  const message = typeof error === "string" ? error : error.message;
  const code = typeof error === "string" ? undefined : error.code;
  const normalized = message.toLowerCase();

  if (
    code === "email_not_confirmed" ||
    normalized.includes("email not confirmed") ||
    normalized.includes("email not verified")
  ) {
    return "Please verify your email before signing in. Check your inbox for the confirmation link.";
  }

  if (normalized.includes("invalid login credentials")) {
    return "Please check your email and password and try again.";
  }

  if (message === "Failed to fetch") {
    return "We couldn't connect right now. Please check your internet connection and try again.";
  }

  return "Something went wrong while signing in. Please try again.";
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const greeting = useMemo(() => HELLOS[Math.floor(Math.random() * HELLOS.length)], []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (error) {
        setError(getLoginNotice(error));
        return;
      }

      navigate("/gate", { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unexpected login error.";
      setError(getLoginNotice(message));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex h-screen w-screen overflow-hidden relative">

      {/* ── Left: brand panel ── */}
      <div
        className="hidden lg:block"
        style={{
          flex: "0 0 60%",
          backgroundImage: "url(/login-logo.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#2a2420",
        }}
      />

      {/* ── Right: login panel ── */}
      <div
        className="flex flex-col justify-center px-10 py-12 w-full lg:w-auto overflow-y-auto"
        style={{ flex: "0 0 40%", backgroundColor: "#fefcf8", borderLeft: "1px solid rgba(80,55,30,0.08)" }}
      >
        <div className="w-full max-w-xs mx-auto">

          {/* Heading */}
          <h1 className="text-2xl font-extrabold mb-1 text-center" style={{ color: NAVY }}>
            Sign in to Bill Lens
          </h1>
          <p className="text-sm mb-8 text-center" style={{ color: NAVY, opacity: 0.5 }}>
            {greeting} Welcome back to Bill Lens
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold mb-1.5 text-center" style={{ color: NAVY }}>
                Email
              </label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-all"
                style={{ border: `1.5px solid ${NAVY}22`, color: NAVY, backgroundColor: "#f8f3ea" }}
                onFocus={(e) => (e.currentTarget.style.border = `1.5px solid ${ORANGE}`)}
                onBlur={(e) => (e.currentTarget.style.border = `1.5px solid ${NAVY}22`)}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1.5 text-center" style={{ color: NAVY }}>
                Password
              </label>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-all"
                style={{ border: `1.5px solid ${NAVY}22`, color: NAVY, backgroundColor: "#f8f3ea" }}
                onFocus={(e) => (e.currentTarget.style.border = `1.5px solid ${ORANGE}`)}
                onBlur={(e) => (e.currentTarget.style.border = `1.5px solid ${NAVY}22`)}
              />
              <div className="mt-1.5 text-right">
                <Link to="/forgot-password" className="text-xs font-semibold" style={{ color: ORANGE }}>
                  Forgot password?
                </Link>
              </div>
            </div>

            {error && (
              <p
                className="text-sm text-center"
                style={{
                  color: "#991b1b",
                }}
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ backgroundColor: ORANGE, boxShadow: "0 3px 12px rgba(96,120,64,0.3)" }}
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="text-sm mt-8 text-center" style={{ color: NAVY, opacity: 0.55 }}>
            Don't have an account?{" "}
            <Link to="/signup" className="font-semibold" style={{ color: ORANGE }}>
              Sign up
            </Link>
          </p>
        </div>
      </div>


    </div>
  );
}
